import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { InstanceSystem, type InstanceVariant } from './InstanceSystem';
import { createRockMaterials } from './RockMaterial';
import type { TerrainData } from './TerrainData';
import type { ColliderGrid } from '../player/Controller';

interface ManifestEntry {
  type: string;
  height: number;
  halfExtents: [number, number];
  tris: number[];
  colliders: { x: number; z: number; y0: number; y1: number; r: number }[];
  capHeight?: number;
}

interface Detection {
  x: number;
  z: number;
  top: number;
  base: number;
  r: number;
  elong: number;
  ang: number;
  kind: 'hoodoo' | 'fin' | 'castle' | 'stub';
}

/** Landmark placements: scene coordinates (from OSM + lidar), top elevation, heading (deg). */
export const LANDMARKS: { name: string; x: number; z: number; top: number; heading: number; clear: number }[] = [
  // Thor's Hammer: fin runs E-W with the hammer at its east end (asset column at local +X ~6 m)
  { name: 'thors_hammer', x: 95, z: 5.5, top: 2397.5, heading: 0, clear: 14 },
  // Two Bridges: fin on the NE crest of the gully, parallel to the trail, windows against the sky
  { name: 'two_bridges', x: 204, z: 179.4, top: 2396, heading: 56.5, clear: 16 },
  // Queen Victoria faces west toward the Queen's Garden trail viewpoint
  { name: 'queen_victoria', x: 430, z: -362, top: 2316, heading: -90, clear: 9 },
];

function mulberry(seed: number) {
  return () => {
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Strip node translation (assets may be laid out side by side in the source file). */
function geometryOf(o: THREE.Mesh): THREE.BufferGeometry {
  const g = o.geometry.clone();
  const m = new THREE.Matrix4().extractRotation(o.matrixWorld);
  const s = new THREE.Vector3().setFromMatrixScale(o.matrixWorld);
  m.scale(s);
  g.applyMatrix4(m);
  if (!g.getAttribute('normal')) g.computeVertexNormals();
  g.computeBoundingSphere();
  return g;
}

export class Hoodoos {
  system!: InstanceSystem;
  boulders: InstanceSystem | null = null;
  manifest!: Record<string, ManifestEntry>;
  placed = 0;
  readonly landmarkPositions = new Map<string, THREE.Vector3>();

  async load(base: string, terrain: TerrainData, colliders: ColliderGrid, shadowUniforms: Record<string, THREE.IUniform>,
    lod: [number, number], maxAniso: number, maxDist = 3000) {
    const [gltf, manifest, dets] = await Promise.all([
      new GLTFLoader().loadAsync(`${base}models/hoodoos.glb`),
      fetch(`${base}models/hoodoos.json`).then((r) => r.json()),
      fetch(`${base}data/hoodoos.json`).then((r) => r.json()),
    ]);
    void maxAniso;
    this.manifest = manifest;
    gltf.scene.updateMatrixWorld(true);
    const meshes = new Map<string, THREE.Mesh>();
    gltf.scene.traverse((o) => {
      if ((o as THREE.Mesh).isMesh) {
        const name = o.name || o.parent?.name || '';
        meshes.set(name, o as THREE.Mesh);
        if (o.parent && o.parent.name && !meshes.has(o.parent.name)) meshes.set(o.parent.name, o as THREE.Mesh);
      }
    });
    const names = Object.keys(manifest);
    const variants: InstanceVariant[] = [];
    const index = new Map<string, number>();
    for (const n of names) {
      const lods: THREE.BufferGeometry[] = [];
      for (let l = 0; l < 3; l++) {
        const m = meshes.get(`${n}_LOD${l}`);
        if (m) lods.push(geometryOf(m));
      }
      if (!lods.length) continue;
      index.set(n, variants.length);
      variants.push({ name: n, lods, radius: lods[0].boundingSphere!.radius });
    }
    const near = terrain.near;
    const { material, depthMaterial } = createRockMaterials(near.normalTex, new THREE.Vector4(near.x0, near.z0, near.size, 0), shadowUniforms);
    this.system = new InstanceSystem(variants, material, depthMaterial, {
      switchDist: [lod[0], lod[1]],
      maxDist,
      sizeRef: 16,
      shadowDists: [70, 200, 520],
    });

    const byType = (pred: (n: string, e: ManifestEntry) => boolean) =>
      names.filter((n) => index.has(n) && pred(n, manifest[n]));
    const fins = byType((n) => /^hoodoo_1[6-9]$/.test(n));
    const castles = byType((n) => /^hoodoo_2[01]$/.test(n));
    const stubs = byType((n) => /^hoodoo_2[23]$/.test(n));
    const singles = byType((n) => /^hoodoo_(0\d|1[0-5])$/.test(n));
    const rnd = mulberry(4242);
    const pick = (list: string[], h: number) => {
      // prefer variants whose native height is close to the target (less stretching)
      let best = list[0], bestScore = Infinity;
      for (const n of list) {
        const sc = Math.abs(Math.log(h / manifest[n].height)) + rnd() * 0.9;
        if (sc < bestScore) { bestScore = sc; best = n; }
      }
      return best;
    };

    const inst0: number[] = [], inst1: number[] = [], vars: number[] = [];
    const addInstance = (name: string, x: number, y: number, z: number, rot: number, sx: number, sy: number, sz: number) => {
      const vi = index.get(name);
      if (vi === undefined) return;
      inst0.push(x, y, z, rot);
      inst1.push(sx, sy, sz, rnd());
      vars.push(vi);
      const e = manifest[name];
      const c = Math.cos(rot), s = Math.sin(rot);
      for (const col of e.colliders ?? []) {
        const lx = col.x * sx, lz = col.z * sz;
        // rotY as in the shader: x' = c*x + s*z, z' = -s*x + c*z
        colliders.add({ x: x + c * lx + s * lz, z: z - s * lx + c * lz, r: col.r * Math.max(sx, sz), y0: y + col.y0 * sy, y1: y + col.y1 * sy });
      }
    };

    // landmarks first
    for (const L of LANDMARKS) {
      if (!index.has(L.name)) continue;
      const e = manifest[L.name];
      const ground = terrain.heightAt(L.x, L.z);
      const h = Math.max(L.top - ground + 2, e.height * 0.6);
      const sy = h / e.height;
      const y = L.top - e.height * sy;
      addInstance(L.name, L.x, y, L.z, THREE.MathUtils.degToRad(-L.heading), sy * 0.95 + 0.05, sy, sy * 0.95 + 0.05);
      this.landmarkPositions.set(L.name, new THREE.Vector3(L.x, y, L.z));
    }
    const nearLandmark = (x: number, z: number) => LANDMARKS.some((L) => Math.hypot(L.x - x, L.z - z) < L.clear);

    for (const d of dets.detections as Detection[]) {
      if (nearLandmark(d.x, d.z)) continue;
      const H = d.top - d.base;
      let list: string[];
      if (d.kind === 'fin' && fins.length) list = fins;
      else if (d.kind === 'castle' && castles.length) list = rnd() < 0.55 ? castles : (fins.length ? fins : singles);
      else if (d.kind === 'stub' && stubs.length) list = stubs;
      else list = singles;
      if (!list.length) continue;
      const name = pick(list, H);
      const e = manifest[name];
      const sy = THREE.MathUtils.clamp((H + 1.5) / e.height, 0.45, 2.6);
      const hx = e.halfExtents[0], hz = e.halfExtents[1];
      let sx: number, sz: number, rot: number;
      if (list === fins) {
        const major = d.r * Math.sqrt(d.elong) * 0.85;
        const minor = d.r / Math.sqrt(d.elong) * 0.8;
        sx = THREE.MathUtils.clamp(major / hx, 0.5, 2.4);
        sz = THREE.MathUtils.clamp(minor / hz, 0.5, 1.8);
        rot = -d.ang;
      } else {
        const target = d.r * 0.72;
        const s = THREE.MathUtils.clamp(target / ((hx + hz) / 2), 0.5, 2.2);
        sx = sz = s;
        rot = rnd() * Math.PI * 2;
      }
      // keep proportions believable
      const lo = sy * 0.5, hi = sy * 1.9;
      sx = THREE.MathUtils.clamp(sx, lo, hi);
      sz = THREE.MathUtils.clamp(sz, lo, hi);
      addInstance(name, d.x, d.base - 0.6, d.z, rot, sx, sy, sz);
    }
    this.system.setInstances(new Float32Array(inst0), new Float32Array(inst1), new Uint16Array(vars));
    this.placed = vars.length;

    // --- boulders: rockfall around formation bases and on steep talus
    const bNames = names.filter((n) => n.startsWith('boulder_') && index.has(n));
    if (bNames.length) {
      const bVariants = bNames.map((n) => variants[index.get(n)!]);
      // fallen blocks: same rock shading but no strata-ledge displacement (that is for in-place beds)
      const bm = createRockMaterials(near.normalTex, new THREE.Vector4(near.x0, near.z0, near.size, 0), shadowUniforms);
      bm.uniforms.uLedge.value = 0;
      bm.uniforms.uRockSat.value = 0.55;
      this.boulders = new InstanceSystem(bVariants, bm.material, bm.depthMaterial, { switchDist: [22], maxDist: 260, sizeRef: 1.2, shadowDists: [45] });
      const b0: number[] = [], b1: number[] = [], bv: number[] = [];
      const addB = (x: number, z: number, scale: number) => {
        const k = Math.floor(rnd() * bNames.length);
        const y = terrain.heightAt(x, z);
        const n = terrain.normalAt(x, z);
        if (n.y < 0.55) return; // too steep to rest on
        b0.push(x, y - 0.05, z, rnd() * Math.PI * 2);
        const s = scale * (0.6 + rnd() * 0.9);
        b1.push(s * (0.8 + rnd() * 0.4), s, s * (0.8 + rnd() * 0.4), rnd());
        bv.push(k);
        const e = manifest[bNames[k]];
        if (e.height * s > 0.6) colliders.add({ x, z, r: Math.max(e.halfExtents[0], e.halfExtents[1]) * s * 0.8, y0: y - 1, y1: y + e.height * s });
      };
      for (const d of dets.detections as Detection[]) {
        const n = Math.floor(rnd() * 3.2);
        for (let i = 0; i < n; i++) {
          const a = rnd() * Math.PI * 2, rr = d.r * (0.9 + rnd() * 1.4);
          addB(d.x + Math.cos(a) * rr, d.z + Math.sin(a) * rr, 0.35 + rnd() * 0.6);
        }
      }
      this.boulders.setInstances(new Float32Array(b0), new Float32Array(b1), new Uint16Array(bv));
    }
  }
}
